import { useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, FloorLabel } from "../motion/Reveal";
import { askEqva } from "../ask/AskEqva";
import { t } from "../../lib/i18n";

/* Four ways in – one per kind of visitor, written the way people actually ask. */
const EXAMPLES = [
  t("Vi skal bytte elektro og automasjon i et prosessanlegg på Vestlandet"),
  t("Hvem av dere jobber med havbruk?"),
  t("Jeg eier et mekanisk verksted med 40 ansatte og vurderer neste steg"),
  t("Jeg er automatiker på Stord. Har dere noe til meg?"),
];

/**
 * The entrance, on the page: one open line to write in, four ways to start. Sending opens «Spør EQVA»
 * with the answer streaming in – the conversation lives in the drawer, so the page itself stays calm.
 * No transcript here, no bubbles, no avatar.
 */
export const AskFloor = () => {
  const [value, setValue] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const text = value.trim();
    if (!text) return;
    askEqva(text);
    setValue("");
    if (e.target?.querySelector) { const ta = e.target.querySelector("textarea"); if (ta) ta.style.height = "auto"; }
  };
  const grow = (el) => { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 160)}px`; };

  return (
    <section data-testid="floor-spor" data-ask-suppress="true" className="bg-surface">
      <div className="wrap floor">
        <div className="grid-12 gap-y-12 lg:items-start">
          <Reveal className="col-span-12 lg:col-span-5">
            <FloorLabel>{t("Spør EQVA")}</FloorLabel>
            <h2 className="mt-4 t-h2 max-w-[15ch] text-balance">{t("Beskriv jobben. Vi finner folkene.")}</h2>
            <p className="mt-6 t-body text-muted max-w-[40ch]">{t("Et anlegg som skal oppgraderes. En rørjobb på Stord. Et selskap du vurderer neste steg for. Beskriv det som til en kollega – så finner vi riktig selskap og riktig person i EQVA.")}</p>
          </Reveal>

          <Reveal delay={100} className="col-span-12 lg:col-span-6 lg:col-start-7 lg:pt-[calc(var(--fs-label)*1.2+1rem)]">
            <form onSubmit={submit} data-testid="ask-floor-form" className="ask-line">
              <label htmlFor="ask-floor-input" className="sr-only">{t("Hva skal gjøres – og hvor?")}</label>
              <textarea
                id="ask-floor-input"
                data-testid="ask-floor-input"
                rows={1}
                maxLength={2000}
                value={value}
                onChange={(e) => { setValue(e.target.value); grow(e.target); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form.requestSubmit(); } }}
                placeholder={t("Hva skal gjøres – og hvor?")}
                className="ask-line-input"
              />
              <button type="submit" data-testid="ask-floor-send" aria-label={t("Spør EQVA")} disabled={!value.trim()} className="ask-line-send">
                <ArrowRight size={22} strokeWidth={1.75} />
              </button>
            </form>

            <p className="mt-10 t-label text-muted">{t("For eksempel")}</p>
            <ul data-testid="ask-floor-examples" className="mt-3 border-t border-line">
              {EXAMPLES.map((ex, i) => (
                <li key={ex} className="border-b border-line">
                  <button type="button" data-testid={`ask-floor-example-${i}`} onClick={() => askEqva(ex)} className="group flex w-full items-start justify-between gap-6 py-4 text-left text-[15px] md:text-[16px] leading-snug text-ink transition-colors hover:text-blue">
                    <span>{ex}</span>
                    <ArrowUpRight size={18} strokeWidth={1.75} className="mt-[1px] shrink-0 text-muted transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-blue" />
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-5 t-small text-muted max-w-[52ch]">{t("Svarene bygger på det EQVA og selskapene har publisert. Et menneske tar over når du vil.")}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
